import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import { googleMapsLoaderOptions } from "../../../config/googleMaps";
import "./History.scss";
import { Bars } from "react-loader-spinner";
import {
  PPE_getApiSync,
  URL_API_PPE,
  getValueFromLocalStorageFunc,
  handleLogOutGlobal_PPE,
  notify,
} from "../../../services/functions";
import { ToastContainer } from "react-toastify";
import storeMenu from "../../../stores";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarToday } from "@mui/icons-material";
import { PersonWalking } from "../../../assets";
import { PersonStanding } from "../../../assets";

const mapContainerStyle = { width: "100%", height: "750px" };
const defaultCenter = { lat: -6.288233, lng: 106.826316 };

interface DeviceData {
  id: number;
  id_device: string;
  latitude: number;
  longitude: number;
  time: string;
  name: string;
  utc?: number | string;
  utc_offset?: number | string;
}
interface Device {
  id_device: string;
  name: string;
  device_data: DeviceData[];
}
type PairedUserOption = { id: number; name: string; deviceCount: number };
type GLL = google.maps.LatLngLiteral;

const BASE_COLORS: string[] = [
  "#34d399", // emerald
  "#60a5fa", // blue
  "#f59e0b", // amber
  "#f472b6", // pink
  "#22d3ee", // cyan
  "#a78bfa", // violet
];

// const START_ICON = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
// const END_ICON = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";

const PERSON_URL = PersonStanding;
const makeIcon = (url: string, size = 60): google.maps.Icon | undefined => {
  const g = (window as any)?.google;

  // console.log("🔍 makeIcon called with:", { url, size });
  // console.log("🔍 Google object:", g);
  // console.log("🔍 Google maps:", g?.maps);
  // console.log("🔍 Google maps Size:", g?.maps?.Size);

  if (!g?.maps) {
    console.warn("⚠️ Google Maps API belum dimuat");
    return undefined;
  }

  if (!g.maps.Size) {
    console.error(
      "❌ g.maps.Size tidak tersedia, Google Maps API mungkin belum sepenuhnya dimuat",
    );
    return undefined;
  }

  if (!g.maps.Point) {
    console.error(
      "❌ g.maps.Point tidak tersedia, Google Maps API mungkin belum sepenuhnya dimuat",
    );
    return undefined;
  }

  if (!g.maps.SymbolPath) {
    console.warn(
      "⚠️ g.maps.SymbolPath tidak tersedia, tapi masih bisa membuat icon dasar",
    );
  }

  try {
    const icon = {
      url,
      scaledSize: new g.maps.Size(size, size),
      anchor: new g.maps.Point(size / 2, size),
    };
    //console.log("✅ Icon berhasil dibuat:", icon);
    return icon;
  } catch (error) {
    console.error("❌ Error membuat icon:", error);
    return undefined;
  }
};

const makeMovingMarkerIcon = (
  color: string,
): google.maps.Symbol | undefined => {
  const g = (window as any)?.google;

  // console.log("🔍 makeMovingMarkerIcon called with color:", color);
  // console.log("🔍 SymbolPath available:", !!g?.maps?.SymbolPath);
  // console.log("🔍 FORWARD_OPEN_ARROW available:", !!g?.maps?.SymbolPath?.FORWARD_OPEN_ARROW);

  if (!g?.maps?.SymbolPath?.FORWARD_OPEN_ARROW) {
    console.warn(
      "⚠️ SymbolPath.FORWARD_OPEN_ARROW tidak tersedia, menggunakan default marker",
    );
    return undefined;
  }

  try {
    const icon = {
      path: g.maps.SymbolPath.FORWARD_OPEN_ARROW,
      scale: 7,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#0f172a",
      strokeWeight: 1.5,
    };
    //console.log("✅ Moving marker icon berhasil dibuat:", icon);
    return icon;
  } catch (error) {
    console.error("❌ Error membuat moving marker icon:", error);
    return undefined;
  }
};

const MIN_SPEED = 5; // km/h
const MAX_SPEED = 100; // km/h
const INIT_SPEED = 40; // km/h

const toLL = (lat: number, lng: number): GLL => ({ lat, lng });
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpLatLng = (from: GLL, to: GLL, t: number): GLL => ({
  lat: lerp(from.lat, to.lat, t),
  lng: lerp(from.lng, to.lng, t),
});

// Haversine distance in KM
const haversineKm = (a: GLL, b: GLL) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s1 =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(s1), Math.sqrt(1 - s1));
  return R * c;
};

const CustomDateInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder }, ref) => (
    <div className="datepicker-input-wrapper" onClick={onClick}>
      <input
        ref={ref}
        value={value}
        onChange={() => {}}
        placeholder={placeholder}
        className="datepicker-input"
        readOnly
      />
      <CalendarToday className="datepicker-icon" fontSize="small" />
    </div>
  ),
);

const formatUtcLabel = (raw?: number | string): string => {
  const parseOffsetToMinutes = (raw?: number | string): number | undefined => {
    if (raw === null || raw === undefined) return undefined;
    if (typeof raw === "number") {
      const abs = Math.abs(raw);
      if (abs <= 14) return Math.round(raw * 60);
      if (abs <= 840) return Math.round(raw);
      if (abs <= 86400) return Math.round(raw / 60);
      return undefined;
    }
    const s = String(raw).trim();
    const m1 = s.match(/^([+-])?(\d{1,2})(?::(\d{2}))?$/); // ±HH[:MM]
    if (m1) {
      const sign = m1[1] === "-" ? -1 : 1;
      const hh = Number(m1[2] || 0);
      const mm = Number(m1[3] || 0);
      return sign * (hh * 60 + mm);
    }
    const n = Number(s);
    if (Number.isFinite(n)) {
      if (Math.abs(n) <= 14) return Math.round(n * 60);
      if (Math.abs(n) <= 840) return Math.round(n);
    }
    return undefined;
  };

  const m = parseOffsetToMinutes(raw) ?? 0;
  const sign = m >= 0 ? "+" : "-";
  const abs = Math.abs(m);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
};

const History: React.FC = () => {
  const { isLoaded } = useJsApiLoader(googleMapsLoaderOptions);
  const [devices, setDevices] = useState<Device[]>([]);
  const [showLoaderMain, setShowLoaderMain] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState<{
    location: DeviceData;
    name: string;
  } | null>(null);

  const [hasFiltered, setHasFiltered] = useState(false);
  const [pairedUserOptions, setPairedUserOptions] = useState<
    PairedUserOption[]
  >([]);
  const [selectedUserId, setSelectedUserId] = useState<string | number>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const datePickerRef = useRef<any>(null);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);

  const getDataHistory = async (userId?: string, date?: string) => {
    let url = `${URL_API_PPE}/v1/location/historymember`;
    if (userId && date) {
      const formattedDate = date.split("T")[0];
      url = `${URL_API_PPE}/v1/location/historymemberfilter?idUser=${userId}&date=${formattedDate}`;
    }

    const token = getValueFromLocalStorageFunc("IOT_TOKEN") ?? "";

    if (!token || token.length < 10) {
      console.error("❌ Invalid or missing token");
      notify(
        "error",
        "Invalid or missing authentication token. Please login again.",
        "TOP_CENTER",
      );
      handleLogOutGlobal_PPE();
      window.location.href = "/login";
      return [];
    }

    try {
      const response = await PPE_getApiSync(
        url,
        null,
        "application/json",
        "GET",
        token,
      );

      if (response?.statusCode) {
        console.error("API Error:", response);

        if (response.statusCode === "403") {
          notify(
            "error",
            "Access denied. You don't have permission to view history.",
            "TOP_CENTER",
          );
        } else if (
          response.statusCode === "401" ||
          response?.detail === "Token expired"
        ) {
          notify(
            "error",
            "Authentication failed. Please login again.",
            "TOP_CENTER",
          );
          setTimeout(() => {
            handleLogOutGlobal_PPE();
            window.location.href = "/login";
          }, 1500);
        } else {
          notify(
            "error",
            response.msg || "Failed to fetch location history.",
            "TOP_CENTER",
          );
        }

        return [];
      }

      if (response?.detail) {
        notify(
          "error",
          "'API Location' " +
            (response?.detail?.[0]?.msg ?? response?.detail ?? ""),
          "TOP_CENTER",
        );
        return [];
      }

      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("❌ API call failed:", error);
      notify(
        "error",
        "Network error. Please check your connection.",
        "TOP_CENTER",
      );
      handleLogOutGlobal_PPE();
      window.location.href = "/login";
      return [];
    }
  };

  const fetchPairedUsersFromDevices = async () => {
    try {
      const token = getValueFromLocalStorageFunc("IOT_TOKEN") ?? "";
      const url = `${URL_API_PPE}/v1/devices/list?limit=1000&offset=0`;
      const res = await PPE_getApiSync(
        url,
        null,
        "application/json",
        "GET",
        token,
      );

      if (!Array.isArray(res)) {
        setPairedUserOptions([]);
        return;
      }

      const map = new Map<number, PairedUserOption>();
      res.forEach((d: any) => {
        const id = Number(d?.id_user);
        if (!id) return;
        const name = d?.name || `User ${id}`;
        const curr = map.get(id) || { id, name, deviceCount: 0 };
        curr.deviceCount += 1;
        map.set(id, curr);
      });

      const opts = Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setPairedUserOptions(opts);
      if (!selectedUserId && opts.length === 1) {
        setSelectedUserId(opts[0].id);
      }
    } catch {
      setPairedUserOptions([]);
    }
  };

  const handleFilter = async () => {
    if (!selectedUserId)
      return notify("error", "User harus dipilih", "TOP_CENTER");
    if (!selectedDate)
      return notify("error", "Tanggal harus dipilih", "TOP_CENTER");

    setHasFiltered(true);
    setSelectedLocation(null);
    setIsFiltering(true);
    setShowLoaderMain(true);

    try {
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const d = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${y}-${m}-${d}`;

      const apiData = await getDataHistory(
        String(selectedUserId),
        formattedDate,
      );
      setDevices(apiData as Device[]);

      if (!apiData || apiData.length === 0) {
        notify(
          "info",
          `Tidak ada data untuk ${
            pairedUserOptions.find((u) => u.id === Number(selectedUserId))
              ?.name || "User"
          } pada ${formatFilterDate(selectedDate)}`,
          "TOP_CENTER",
        );
      } else {
        notify(
          "success",
          `Berhasil memuat ${apiData.length} device untuk ${formatFilterDate(
            selectedDate,
          )}`,
          "TOP_CENTER",
        );
      }
    } catch {
      notify("error", "Terjadi kesalahan saat memfilter data", "TOP_CENTER");
    } finally {
      setIsFiltering(false);
      setShowLoaderMain(false);
    }
  };

  const handleReset = () => {
    setHasFiltered(false);
    setSelectedLocation(null);
    setDevices([]);
    setSelectedUserId("");
    setSelectedDate(null);
    setIsPlaying(false);
    setSimTimeMs(0);
    setSimProgress(0);
    notify("success", "Filter direset", "TOP_CENTER");
  };

  const formatDateTime = (s: string) => {
    try {
      const d = new Date(s);
      return d.toLocaleString("id-ID", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "Asia/Jakarta",
      });
    } catch {
      return s;
    }
  };
  const formatFilterDate = (d: Date) =>
    d.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  useEffect(() => {
    const init = async () => {
      setShowLoaderMain(true);
      await fetchPairedUsersFromDevices();
      setShowLoaderMain(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      storeMenu.dispatch({ type: "titleicon", text: "History Location" });
      storeMenu.dispatch({
        type: "breadcrumbs",
        text: [
          { key: "Dashboard", value: "Dashboard" },
          { key: "Sites", value: "History Location" },
        ],
      });
    }, 100);
  }, []);

  // Monitor Google Maps API loading status
  useEffect(() => {
    if (isLoaded) {
      setGoogleMapsReady(true);
    }
  }, [isLoaded]);

  useEffect(() => {
    const checkGoogleMaps = () => {
      const g = (window as any)?.google;
      const isReady = !!(
        g?.maps?.Size &&
        g?.maps?.Point &&
        g?.maps?.SymbolPath
      );
      // console.log("🔍 Checking Google Maps API status:", {
      //   google: !!g,
      //   maps: !!g?.maps,
      //   Size: !!g?.maps?.Size,
      //   Point: !!g?.maps?.Point,
      //   SymbolPath: !!g?.maps?.SymbolPath,
      //   FORWARD_OPEN_ARROW: !!g?.maps?.SymbolPath?.FORWARD_OPEN_ARROW,
      //   isReady
      // });

      if (isReady && !googleMapsReady) {
        //console.log("✅ Google Maps API siap digunakan (termasuk SymbolPath)");
        setGoogleMapsReady(true);
      } else if (!isReady && googleMapsReady) {
        //console.log("⚠️ Google Maps API tidak siap (SymbolPath mungkin belum dimuat)");
        setGoogleMapsReady(false);
      }
    };

    // Check immediately
    checkGoogleMaps();

    // Check periodically until ready
    const interval = setInterval(() => {
      checkGoogleMaps();
      if (googleMapsReady) {
        clearInterval(interval);
      }
    }, 100);

    // Cleanup
    return () => clearInterval(interval);
  }, [googleMapsReady]);

  const precomputed = useMemo(() => {
    const out: Record<
      string,
      {
        id_device: string;
        name: string;
        points: GLL[];
        raw: DeviceData[];
        segKm: number[];
        cumKm: number[];
        totalKm: number;
        tMin?: number;
        tMax?: number;
      }
    > = {};

    devices.forEach((d) => {
      const sorted = [...d.device_data]
        .filter(
          (p) =>
            typeof p.latitude === "number" &&
            typeof p.longitude === "number" &&
            p.time,
        )
        .sort(
          (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
        );

      if (sorted.length === 0) return;

      const pts = sorted.map((p) => toLL(p.latitude, p.longitude));

      const segKm: number[] = [];
      const cumKm: number[] = [0];
      for (let i = 1; i < pts.length; i++) {
        const dist = haversineKm(pts[i - 1], pts[i]);
        segKm.push(dist);
        cumKm[i] = cumKm[i - 1] + dist;
      }
      const tMin = new Date(sorted[0].time).getTime();
      const tMax = new Date(sorted[sorted.length - 1].time).getTime();

      out[d.id_device] = {
        id_device: d.id_device,
        name: d.name,
        points: pts,
        raw: sorted,
        segKm,
        cumKm,
        totalKm: cumKm[cumKm.length - 1] ?? 0,
        tMin: isFinite(tMin) ? tMin : undefined,
        tMax: isFinite(tMax) ? tMax : undefined,
      };
    });

    return out;
  }, [devices]);

  // ================== ANIMATION ==================
  const [speedKmh, setSpeedKmh] = useState<number>(INIT_SPEED);
  const [isPlaying, setIsPlaying] = useState(false);
  const [simTimeMs, setSimTimeMs] = useState(0); // global sim clock
  const [simProgress, setSimProgress] = useState(0); // 0..1 timeline scrubber
  const lastStepRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const cumTimesByDevice = useMemo(() => {
    const out: Record<string, number[]> = {};
    Object.values(precomputed).forEach((t) => {
      const times: number[] = [0];
      for (let i = 0; i < t.segKm.length; i++) {
        const segHours = t.segKm[i] / Math.max(speedKmh, 1);
        const segMs = segHours * 3600 * 1000;
        times.push(times[i] + segMs);
      }
      out[t.id_device] = times;
    });
    return out;
  }, [precomputed, speedKmh]);

  // durasi sim global
  const maxDurationMs = useMemo(() => {
    const arr = Object.values(cumTimesByDevice).map(
      (ct) => ct[ct.length - 1] || 0,
    );
    return arr.length ? Math.max(...arr) : 0;
  }, [cumTimesByDevice]);

  // posisi berjalan per device
  const [currentPosByDevice, setCurrentPosByDevice] = useState<
    Record<string, GLL | null>
  >({});
  const [trailOn, setTrailOn] = useState(true);
  const [followId, setFollowId] = useState<string>("none");

  const visibleIndexForDevice = (id: string) => {
    const pts = precomputed[id]?.points || [];
    if (pts.length <= 1) return 0;

    const cumTimes = cumTimesByDevice[id] || [0];
    const T = cumTimes[cumTimes.length - 1] || 0;
    const t = Math.min(simTimeMs, T);

    let i = 0;
    while (i < cumTimes.length - 1 && t >= cumTimes[i + 1]) i++;
    return i;
  };

  // posisi berjalan saat simTimeMs berubah
  useEffect(() => {
    const next: Record<string, GLL | null> = {};

    Object.values(precomputed).forEach((track) => {
      const id = track.id_device;
      const pts = track.points;

      if (followId !== "none" && followId !== id) {
        next[id] = pts.length ? pts[0] : null;
        return;
      }

      if (pts.length === 0) {
        next[id] = null;
        return;
      }
      if (pts.length === 1) {
        next[id] = pts[0];
        return;
      }

      const cumTimes = cumTimesByDevice[id] || [0];
      const T = cumTimes[cumTimes.length - 1] || 0;
      const t = Math.min(simTimeMs, T);

      let i = 0;
      while (i < cumTimes.length - 1 && !(t < cumTimes[i + 1])) i++;

      if (i >= pts.length - 1) {
        next[id] = pts[pts.length - 1];
        return;
      }

      const segStartT = cumTimes[i];
      const segEndT = cumTimes[i + 1];
      const segDur = Math.max(segEndT - segStartT, 1);
      const alpha = (t - segStartT) / segDur;

      next[id] = lerpLatLng(pts[i], pts[i + 1], alpha);
    });

    setCurrentPosByDevice(next);
  }, [simTimeMs, precomputed, cumTimesByDevice, followId]);

  // sinkron dengan scrubber
  useEffect(() => {
    setSimTimeMs(simProgress * maxDurationMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simProgress, maxDurationMs]);

  useEffect(() => {
    setIsPlaying(false);
    setSimTimeMs(0);
    setSimProgress(0);
    const init: Record<string, GLL | null> = {};
    Object.values(precomputed).forEach(
      (t) => (init[t.id_device] = t.points[0] ?? null),
    );
    setCurrentPosByDevice(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(precomputed).join("|")]);

  useEffect(() => {
    if (!isPlaying || maxDurationMs <= 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastStepRef.current = null;
      return;
    }

    const step = (ts: number) => {
      if (lastStepRef.current == null) lastStepRef.current = ts;
      const dt = ts - lastStepRef.current;
      lastStepRef.current = ts;

      const nextSim = Math.min(simTimeMs + dt, maxDurationMs);
      setSimTimeMs(nextSim);
      setSimProgress(nextSim / maxDurationMs);

      if (
        followId !== "none" &&
        currentPosByDevice[followId] &&
        mapRef.current
      ) {
        mapRef.current.panTo(currentPosByDevice[followId] as GLL);
      }

      if (nextSim >= maxDurationMs) {
        setIsPlaying(false);
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastStepRef.current = null;
    };
  }, [isPlaying, maxDurationMs, simTimeMs, followId, currentPosByDevice]);

  const totalKmAll = useMemo(
    () => Object.values(precomputed).reduce((s, t) => s + (t.totalKm || 0), 0),
    [precomputed],
  );
  const dataDurationMs = useMemo(() => {
    let minT = Infinity,
      maxT = -Infinity;
    devices.forEach((d) => {
      d.device_data.forEach((p) => {
        const t = new Date(p.time).getTime();
        if (!isFinite(t)) return;
        minT = Math.min(minT, t);
        maxT = Math.max(maxT, t);
      });
    });
    return isFinite(minT) && isFinite(maxT) && maxT > minT ? maxT - minT : 0;
  }, [devices]);

  const msToHMS = (ms: number) => {
    const s = Math.round(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0",
    )}:${String(ss).padStart(2, "0")}`;
  };

  // trail sampai posisi sekarang
  const trailPointsFor = (t: { points: GLL[]; id_device: string }) => {
    const pts = t.points;
    if (pts.length <= 1) return pts;

    const cumTimes = cumTimesByDevice[t.id_device] || [0];
    const T = cumTimes[cumTimes.length - 1] || 0;
    const timeNow = Math.min(simTimeMs, T);

    let i = 0;
    while (i < cumTimes.length - 1 && !(timeNow < cumTimes[i + 1])) i++;

    if (i >= pts.length - 1) return pts;

    const segStartT = cumTimes[i];
    const segEndT = cumTimes[i + 1];
    const segDur = Math.max(segEndT - segStartT, 1);
    const alpha = (timeNow - segStartT) / segDur;
    const cur = lerpLatLng(pts[i], pts[i + 1], alpha);

    return [...pts.slice(0, i + 1), cur];
  };

  const showFor = (id: string) => followId === "none" || followId === id;

  return (
    <div className="ppe-anly">
      {/* FILTER */}
      <div className="history-filter-section">
        <h3 className="filter-title">Filter History Location</h3>

        <div className="filter-container">
          {/* User */}
          <div className="filter-item">
            <label htmlFor="idUser" className="filter-label">
              ID User:
            </label>
            <select
              id="idUser"
              className="filter-input"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleFilter()}
            >
              <option value="">Pilih User</option>
              {pairedUserOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div className="filter-item">
            <label htmlFor="date" className="filter-label">
              Tanggal:
            </label>
            <div className="datepicker-container">
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date) => setSelectedDate(date)}
                dateFormat="MM/dd/yyyy"
                className="datepicker-input"
                placeholderText="mm / dd / yyyy"
                maxDate={new Date()}
                ref={datePickerRef}
                popperPlacement="bottom-start"
                showMonthDropdown={true}
                showYearDropdown={true}
                dropdownMode="select"
                yearDropdownItemNumber={10}
                onClickOutside={() => {}}
                popperModifiers={[
                  {
                    name: "preventOverflow",
                    options: { boundary: document.body, padding: 8 },
                  },
                ]}
                portalId="datepicker-portal"
                popperContainer={({ children }) => (
                  <div className="datepicker-popper-container">{children}</div>
                )}
                customInput={<CustomDateInput />}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="filter-buttons">
            <button
              onClick={handleFilter}
              className="filter-btn filter-btn-primary"
              disabled={isFiltering}
            >
              {isFiltering ? (
                <Bars width="20" height="20" visible color="#ffffff" />
              ) : (
                "Filter"
              )}
            </button>
            <button
              onClick={handleReset}
              className="filter-btn filter-btn-secondary"
              disabled={isFiltering}
            >
              {isFiltering ? (
                <Bars width="20" height="20" visible color="#ffffff" />
              ) : (
                "Reset"
              )}
            </button>
          </div>
        </div>

        {/* STAT BAR */}
        {hasFiltered && devices.length > 0 && (
          <div className="filter-result-bar">
            <span className="result-text">
              <strong>Hasil:</strong> {devices.length} device
              <span>
                Total jarak: <b>{totalKmAll.toFixed(2)} km</b>
              </span>
              <span>
                Durasi data:{" "}
                <b>{dataDurationMs ? msToHMS(dataDurationMs) : "-"}</b>
              </span>
              <span>
                Durasi simulasi ({speedKmh} km/j):{" "}
                <b>{msToHMS(maxDurationMs)}</b>
              </span>
            </span>
          </div>
        )}
      </div>

      {/* LOADER / EMPTY */}
      {hasFiltered && showLoaderMain && (
        <div className="ppe-anly-ppe-loader-main">
          <Bars
            width="80"
            height="80"
            visible={showLoaderMain}
            color="#4fa94d"
          />
        </div>
      )}
      {hasFiltered && !showLoaderMain && devices.length === 0 && (
        <div className="no-data-section">
          <div className="no-data-content">
            <img
              src={require("../../../assets/images/No data-amico storyset.com.png")}
              alt="No Data"
              className="no-data-image"
            />
            <h3 className="no-data-title">Tidak Ada Data</h3>
            <p className="no-data-description">
              {selectedUserId && selectedDate
                ? `Tidak ada data lokasi untuk ${
                    pairedUserOptions.find(
                      (u) => u.id === Number(selectedUserId),
                    )?.name || "User"
                  } pada ${formatFilterDate(selectedDate)}`
                : selectedUserId
                  ? "Silakan pilih tanggal untuk memfilter data"
                  : "Belum ada data lokasi yang tersedia"}
            </p>
          </div>
        </div>
      )}

      {/* MAP + CONTROLS */}
      {hasFiltered && !showLoaderMain && devices.length > 0 && (
        <>
          {/* Google Maps API Status Indicator */}
          {!googleMapsReady && (
            <div className="google-maps-loading-indicator">
              <span>⏳ Memuat Google Maps API... Harap tunggu sebentar.</span>
            </div>
          )}

          <div className="anim-controls">
            {/* Timeline Scrubber */}
            <div className="timeline-scrubber-container">
              <label
                htmlFor="timeline-scrubber"
                className="filter-label timeline-scrubber-label"
              >
                Timeline: <b>{Math.round(simProgress * 100)}%</b>
              </label>
              <input
                id="timeline-scrubber"
                type="range"
                min={0}
                max={1000}
                value={Math.round(simProgress * 1000)}
                onChange={(e) => {
                  const v = Number(e.target.value) / 1000;
                  setIsPlaying(false);
                  setSimProgress(v);
                }}
                aria-label="Timeline scrubber untuk mengontrol progress simulasi"
              />
            </div>

            {/* Play/Pause */}
            <div className="control-buttons">
              <button
                className="filter-btn filter-btn-primary"
                onClick={() => setIsPlaying((p) => !p)}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                className="filter-btn filter-btn-secondary"
                onClick={() => {
                  setIsPlaying(false);
                  setSimTimeMs(0);
                  setSimProgress(0);
                  const init: Record<string, GLL | null> = {};
                  Object.values(precomputed).forEach(
                    (t) => (init[t.id_device] = t.points[0] ?? null),
                  );
                  setCurrentPosByDevice(init);
                }}
              >
                Reset
              </button>
            </div>

            {/* Speed (km/jam) */}
            <div className="speed-control">
              <label htmlFor="speed-range" className="filter-label speed-label">
                Speed:
              </label>
              <input
                id="speed-range"
                type="range"
                min={MIN_SPEED}
                max={MAX_SPEED}
                step={1}
                value={speedKmh}
                onChange={(e) => setSpeedKmh(Number(e.target.value))}
                aria-label="Kontrol kecepatan simulasi dalam kilometer per jam"
              />
              <span className="speed-display">{speedKmh} km/j</span>
            </div>

            {/* Follow */}
            <div className="follow-control">
              <label
                htmlFor="follow-select"
                className="filter-label follow-label"
              >
                Follow:
              </label>
              <select
                id="follow-select"
                className="filter-input follow-select"
                value={followId}
                onChange={(e) => setFollowId(e.target.value)}
                aria-label="Pilih device untuk diikuti pada peta"
              >
                <option value="none">— None —</option>
                {devices.map((d) => (
                  <option key={`f-${d.id_device}`} value={d.id_device}>
                    {precomputed[d.id_device]?.name || "Device"} ({d.id_device})
                  </option>
                ))}
              </select>
            </div>

            {/* Trail toggle */}
            <div className="trail-toggle">
              <input
                id="trail"
                type="checkbox"
                checked={trailOn}
                onChange={(e) => setTrailOn(e.target.checked)}
              />
              <label htmlFor="trail" className="filter-label trail-label">
                Show trail
              </label>
            </div>
          </div>

          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={8}
              center={defaultCenter}
              onLoad={(map) => {
                //console.log("✅ Google Map instance berhasil dimuat");
                mapRef.current = map;
                const allPoints: GLL[] = [];
                Object.values(precomputed).forEach((t) =>
                  allPoints.push(...t.points),
                );
                if (allPoints.length && (window as any).google) {
                  const bounds = new (window as any).google.maps.LatLngBounds();
                  allPoints.forEach((p) => bounds.extend(p));
                  map.fitBounds(bounds);
                }
              }}
            >
              {/* TRAIL */}
              {trailOn &&
                devices.map((d, idx) => {
                  if (!showFor(d.id_device)) return null;
                  const t = precomputed[d.id_device];
                  if (!t || t.points.length < 2) return null;
                  const color = BASE_COLORS[idx % BASE_COLORS.length];
                  const trail = trailPointsFor({
                    points: t.points,
                    id_device: t.id_device,
                  });
                  if (trail.length < 2) return null;
                  return (
                    <Polyline
                      key={`trail-${d.id_device}`}
                      path={trail}
                      options={{
                        strokeColor: color,
                        strokeOpacity: 1,
                        strokeWeight: 5,
                        geodesic: true,
                      }}
                    />
                  );
                })}

              {/* START / END markers */}
              {devices.map((d) => {
                if (!showFor(d.id_device)) return null;

                const t = precomputed[d.id_device];
                if (!t || !t.points.length) return null;

                const start = t.points[0];
                const end = t.points[t.points.length - 1];

                const startRaw = t.raw[0];
                const endRaw = t.raw[t.raw.length - 1];

                const visibleIdx = visibleIndexForDevice(d.id_device);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const showStart = visibleIdx >= 1;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const showEnd = visibleIdx >= t.points.length - 1;

                if (!googleMapsReady) {
                  //console.log("⏳ Menunggu Google Maps API siap untuk render marker");
                  return null;
                }

                return (
                  <React.Fragment key={`pins-${d.id_device}`}>
                    {/* {showStart && <Marker position={start} icon={START_ICON} />}
                    {showEnd && <Marker position={end} icon={END_ICON} />} */}
                    <Marker
                      position={start}
                      icon={makeIcon(PERSON_URL, 60)}
                      onClick={() =>
                        setSelectedLocation({
                          location: startRaw,
                          name: t.name,
                        })
                      }
                    />
                    <Marker
                      position={end}
                      icon={makeIcon(PERSON_URL, 60)}
                      onClick={() =>
                        setSelectedLocation({
                          location: endRaw,
                          name: t.name,
                        })
                      }
                    />
                  </React.Fragment>
                );
              })}

              {/* MOVING MARKERS */}
              {devices.map((d, idx) => {
                if (!showFor(d.id_device)) return null;

                const pos = currentPosByDevice[d.id_device];
                if (!pos) return null;

                // Hanya render jika Google Maps API siap
                if (!googleMapsReady) return null;

                const color = BASE_COLORS[idx % BASE_COLORS.length];

                // Menggunakan fungsi helper yang lebih aman
                const iconConfig = makeMovingMarkerIcon(color);

                return (
                  <Marker
                    key={`mv-${d.id_device}`}
                    position={pos}
                    icon={iconConfig}
                  />
                );
              })}

              {/* MARKER TITIK (progresif per segmen) */}
              {devices.map((device) => {
                if (!showFor(device.id_device)) return null;

                const t = precomputed[device.id_device];
                if (!t || t.points.length === 0) return null;

                const visibleIdx = visibleIndexForDevice(device.id_device);
                if (visibleIdx < 1) return null;

                if (!googleMapsReady) {
                  //console.log("⏳ Menunggu Google Maps API siap untuk render progressive markers");
                  return null;
                }

                return (
                  <React.Fragment key={`prog-${device.id_device}`}>
                    {t.raw
                      .slice(1, Math.min(visibleIdx + 1, t.raw.length))
                      .map((loc) => (
                        <Marker
                          key={`p-${device.id_device}-${loc.id}`}
                          position={{ lat: loc.latitude, lng: loc.longitude }}
                          onClick={() =>
                            setSelectedLocation({
                              location: loc,
                              name: device.name,
                            })
                          }
                          icon={makeIcon(PersonWalking, 60)}
                        />
                      ))}
                  </React.Fragment>
                );
              })}

              {/* InfoWindow */}
              {selectedLocation && (
                <InfoWindow
                  position={{
                    lat: selectedLocation.location.latitude,
                    lng: selectedLocation.location.longitude,
                  }}
                  onCloseClick={() => setSelectedLocation(null)}
                >
                  <div>
                    <p>
                      <strong>Device ID:</strong>{" "}
                      {selectedLocation.location.id_device}
                    </p>
                    <p>
                      <strong>User:</strong> {selectedLocation.name}
                    </p>

                    {/* Time + UTC label (tanpa mengubah jamnya) */}
                    <p>
                      <strong>Time:</strong>{" "}
                      {formatDateTime(selectedLocation.location.time)}{" "}
                      {(() => {
                        const off =
                          selectedLocation.location.utc ??
                          selectedLocation.location.utc_offset;
                        // jika ada offset, tampilkan label UTC±HH:MM
                        return off !== undefined && off !== null ? (
                          <small className="text-muted">
                            ({formatUtcLabel(off)})
                          </small>
                        ) : null;
                      })()}
                    </p>

                    {selectedDate && (
                      <p>
                        <strong>Filter Date:</strong>{" "}
                        {formatFilterDate(selectedDate)}
                      </p>
                    )}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : null}
        </>
      )}

      <ToastContainer draggable pauseOnHover />

      {/* Portal container untuk date picker */}
      {/* <div
        id="datepicker-portal"
        style={{
          position: "fixed",
          zIndex: 999999,
          top: 0,
          left: 0,
          pointerEvents: "auto",
        }}
      /> */}
    </div>
  );
};

export default History;
